import type { ReferralActivityItem } from '@/types/referral';
import { TrendingUp } from 'lucide-react';
import { useMemo } from 'react';

interface StreakCardProps {
  activity: ReferralActivityItem[];
  totalReferrals: number;
}

function countRecentActivity(activity: ReferralActivityItem[], days: number) {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return activity.filter((item) => new Date(item.createdAt).getTime() >= cutoff)
    .length;
}

export const StreakCard = ({ activity, totalReferrals }: StreakCardProps) => {
  const weekCount = useMemo(() => countRecentActivity(activity, 7), [activity]);

  const data = useMemo(() => {
    const points = Array.from({ length: 7 }, (_, index) => {
      const dayStart = new Date();
      dayStart.setHours(0, 0, 0, 0);
      dayStart.setDate(dayStart.getDate() - (6 - index));

      const dayEnd = new Date(dayStart);
      dayEnd.setDate(dayEnd.getDate() + 1);

      return activity.filter((item) => {
        const created = new Date(item.createdAt);
        return created >= dayStart && created < dayEnd;
      }).length;
    });

    return points.some((value) => value > 0) ? points : [0, 0, 0, 0, 0, 0, 0];
  }, [activity]);

  const max = Math.max(...data, 1);
  const w = 100;
  const h = 48;
  const pts = data
    .map(
      (value, index) =>
        `${(index / (data.length - 1)) * w},${h - (value / max) * h}`
    )
    .join(' ');

  const onRoll = weekCount > 0 || totalReferrals > 0;

  return (
    <div className="streak-card">
      <div className="row-h">
        <div>
          <div className="lbl" style={{ marginBottom: 2 }}>
            This week
          </div>
          <div className="v">{weekCount} new friends</div>
          {onRoll && (
            <div className="delta">
              <TrendingUp className="size-3.5" />
              {totalReferrals} total referrals
            </div>
          )}
        </div>
        {onRoll && (
          <span className="status-pill p-active" style={{ flexShrink: 0 }}>
            On a roll
          </span>
        )}
      </div>
      <svg
        className="spark"
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="refer-earn-spark" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#22B573" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#22B573" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polyline
          points={`0,${h} ${pts} ${w},${h}`}
          fill="url(#refer-earn-spark)"
        />
        <polyline
          points={pts}
          fill="none"
          stroke="#22B573"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {data.map((value, index) => (
          <circle
            key={index}
            cx={(index / (data.length - 1)) * w}
            cy={h - (value / max) * h}
            r={index === data.length - 1 ? 2.5 : 1.5}
            fill="#22B573"
          />
        ))}
      </svg>
      <div className="week">
        <span>M</span>
        <span>T</span>
        <span>W</span>
        <span>T</span>
        <span>F</span>
        <span>S</span>
        <span style={{ color: 'var(--rne-brand-green-press)' }}>S</span>
      </div>
    </div>
  );
};
